import { IconButton, Portal, Tooltip, useBoolean } from '@chakra-ui/react';
import { ReactComponent as InteractiveIcon } from 'assets/icons/interactive.svg';
import { WIDGETBASE_CLASS } from 'constants/bounding-box';
import { INTERACTIVE_INFOGRAPH_PAGE } from 'constants/interactiveElement';
import { INTERACTION_PREVIEWED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectHasInfographLoaded } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INTERACTIVE_ELEMENT_WRAPPER_CLASSNAME } from 'widgets/sdk/InteractiveElements/InteractiveElement.config';

import { HotspotAnimation } from './HotspotAnimation';
import { HOTSPOT_ANIMATION_DURATION_MS, HOTSPOT_SIZE_PX } from './InteractiveHotspot.config';
import { checkPageHasInteractiveElements } from './InteractiveHotspot.helpers';

type InteractiveHotspotLocation = {
  top: number;
  left: number;
};

/**
 * INTERACTIVE HOTSPOT TRIGGER COMPONENT
 *
 * Renders button to show interactive hotspots on the page based on elements with INTERACTIVE_ELEMENT_WRAPPER_CLASSNAME
 * Hidden if no interactive elements are present on the page.
 *
 * Automatically triggers hotspot animation on first page with interactions.
 */
export const InteractiveHotspotTrigger = () => {
  const activePageId = useAppSelector(selectActivePage);
  const [hasInteractiveElements, setHasInteractiveElements] = useState(false);

  // Flag to indicate if hotspots have already been displayed on the page.
  const hasRendered = useRef(false);

  const setHasRendered = useCallback(() => {
    hasRendered.current = true;
  }, []);

  // Show/hide trigger on page change
  useEffect(() => {
    const pageHasInteractiveElements = checkPageHasInteractiveElements(activePageId);
    setHasInteractiveElements(pageHasInteractiveElements);
  }, [activePageId]);

  if (!hasInteractiveElements) return null;

  return <InteractiveHotspot isInitialRender={!hasRendered.current} setInitialRender={setHasRendered} />;
};

interface InteractiveHotspotProps {
  isInitialRender: boolean;
  setInitialRender: () => void;
}

const InteractiveHotspot = ({ isInitialRender, setInitialRender }: InteractiveHotspotProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'interactive' });

  const activePageId = useAppSelector(selectActivePage);
  const hasInfographLoaded = useAppSelector(selectHasInfographLoaded);

  // Flag to show/hide hotspots
  const [show, setShow] = useBoolean();

  // Hotspot locations
  const hotspots = useRef<InteractiveHotspotLocation[]>([]);

  // Get the interactive elements and their positions relative to the page, show hotspots
  const showHotspots = useCallback(() => {
    setInitialRender();
    const interactiveElements = document.querySelectorAll(`.${INTERACTIVE_ELEMENT_WRAPPER_CLASSNAME}`);
    if (!interactiveElements || !interactiveElements.length) {
      hotspots.current = [];
      return;
    }

    const newHotspots = Array.from(interactiveElements)
      .map((element: Element) => {
        const widgetEl = element.closest(`.${WIDGETBASE_CLASS}`) as HTMLElement;
        if (!widgetEl) return null;

        const top = widgetEl.offsetTop;
        const left = widgetEl.offsetLeft;
        const width = widgetEl.offsetWidth;
        const height = widgetEl.offsetHeight;

        if (
          typeof top !== 'number' ||
          typeof left !== 'number' ||
          typeof width !== 'number' ||
          typeof height !== 'number'
        ) {
          return null;
        }

        return {
          top: top + height / 2 - HOTSPOT_SIZE_PX / 2,
          left: left + width / 2 - HOTSPOT_SIZE_PX / 2,
        };
      })
      .filter(Boolean) as InteractiveHotspotLocation[];
    hotspots.current = newHotspots;

    setShow.on();
  }, [setShow, setInitialRender]);

  // Automatically show hotspots after infograph has loaded and if it's not the initial render
  useEffect(() => {
    if (!hasInfographLoaded || !isInitialRender) return;

    // Wait for widgets to finish loading before showing hotspots
    setTimeout(showHotspots, 100);
  }, [hasInfographLoaded, isInitialRender, showHotspots]);

  // Hide hotspot when page changes
  useEffect(() => {
    setShow.off();
  }, [activePageId, setShow]);

  // Hide hotspots after 3s
  useEffect(() => {
    if (!show) return;

    const timer = setTimeout(() => {
      setShow.off();
    }, HOTSPOT_ANIMATION_DURATION_MS);
    return () => clearTimeout(timer);
  }, [show, setShow]);

  const onClick = () => {
    showHotspots();
    Mixpanel.track(INTERACTION_PREVIEWED.eventName);
  };

  // Should attach the hotspots to the page container
  const getInfographRef = () => ({ current: document.getElementById(INTERACTIVE_INFOGRAPH_PAGE) || document.body });

  const label = t('showInteractions');
  return (
    <>
      <Tooltip label={label} aria-label={label} placement='top' isDisabled={!!show} hasArrow>
        <IconButton
          size='sm'
          aria-label={label}
          onClick={onClick}
          _hover={{ opacity: 0.7 }}
          background='radial-gradient(70% 68% at 50% 50%, #FFC42A 0%, #D75C97 100%)'
          icon={<InteractiveIcon width={24} height={24} color='black' />}
          isDisabled={show}
        />
      </Tooltip>
      {show && (
        <Portal containerRef={getInfographRef()}>
          {hotspots.current.map((hotspot, index) => (
            <HotspotAnimation key={index} topPx={hotspot.top} leftPx={hotspot.left} />
          ))}
        </Portal>
      )}
    </>
  );
};
